// Social sign-in (Google, Apple, Facebook) without Authentik, like the legacy
// dental-book-frontend: the provider's own JS SDK returns a token from a popup and
// POST /api/v1/auth/social exchanges it for the same session as POST /api/v1/auth/login.
//
// The request/response shape and the user-facing messages live ONLY in this module.
import axios from 'axios';
import config from '../config';
import {fromLoginResponse, saveSession} from './legacy';
import {newIdempotencyKey} from './signup';

const SOCIAL_PATH = '/api/v1/auth/social';

export const SOCIAL_PROVIDERS = Object.freeze(['google', 'apple', 'facebook']);
export const PROVIDER_LABELS = Object.freeze({google: 'Google', apple: 'Apple', facebook: 'Facebook'});

const label = (provider) => PROVIDER_LABELS[provider] || 'Social';

export const socialMessages = (provider) => {
  const name = label(provider);
  return {
    unverified: `We couldn't verify your ${name} sign-in. Please try again.`,
    conflict: 'An account with this email already exists. Sign in with your password.',
    invalid: `Your ${name} account didn't share the details we need. Please try again.`,
    unavailable: `${name} sign-in isn't available right now.`,
    popupBlocked: `Your browser blocked the ${name} sign-in window. Allow pop-ups for this site and try again.`,
    generic: `${name} sign-in failed. Please try again.`,
  };
};

/** True when the runtime config has every public identifier the provider's SDK needs. */
export const providerConfigured = (provider, cfg = config) => {
  if (provider === 'google') return Boolean(cfg.googleClientId);
  if (provider === 'apple') return Boolean(cfg.appleClientId && cfg.appleRedirectUri);
  if (provider === 'facebook') return Boolean(cfg.facebookAppId);
  return false;
};

/** "Apple sign-in isn't…" / "Google and Apple sign-in aren't available right now." */
export const unavailableNote = (providers) => {
  const names = providers.map(label);
  if (!names.length) return '';
  if (names.length === 1) return `${names[0]} sign-in isn't available right now.`;
  const list = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${list} sign-in aren't available right now.`;
};

const text = (v) => (typeof v === 'string' ? v.trim() : '');

/** `{provider, token, first_name?, last_name?}` -> request body; blank names are left out. */
export const toSocialRequest = ({provider, token, first_name: first, last_name: last}) => {
  if (!SOCIAL_PROVIDERS.includes(provider)) throw new Error(`Unknown social provider: ${provider}`);
  if (typeof token !== 'string' || !token) throw new Error('Missing provider token');
  const body = {provider, token};
  if (text(first)) body.first_name = text(first);
  if (text(last)) body.last_name = text(last);
  return body;
};

/**
 * Apple only sends the user's name on the first consent (`user.name`), never in the id_token,
 * so it is forwarded once with the token.
 */
export const appleNames = (response) => {
  const name = (response && response.user && response.user.name) || {};
  const names = {};
  if (text(name.firstName)) names.first_name = text(name.firstName);
  if (text(name.lastName)) names.last_name = text(name.lastName);
  return names;
};

// Provider SDK failures -> 'cancelled' | 'popup_blocked' | 'unavailable' | 'failed'.

/** @react-oauth/google: OAuth error responses (`error`) and non-OAuth errors (`type`). */
export const googleErrorKind = (err) => {
  const code = err && (err.type || err.error);
  if (code === 'popup_closed' || code === 'access_denied') return 'cancelled';
  if (code === 'popup_failed_to_open') return 'popup_blocked';
  return 'failed';
};

/** Sign in with Apple JS: `{error: 'popup_closed_by_user' | 'user_cancelled_authorize' | …}`. */
export const appleErrorKind = (err) => {
  const code = err && err.error;
  if (code === 'popup_closed_by_user' || code === 'user_cancelled_authorize') return 'cancelled';
  if (code === 'popup_blocked_by_browser') return 'popup_blocked';
  return 'failed';
};

/** @greatsumini/react-facebook-login `onFail({status})`. */
export const facebookErrorKind = (err) => {
  const status = err && err.status;
  if (status === 'loginCancelled') return 'cancelled';
  if (status === 'facebookNotLoaded') return 'unavailable';
  return 'failed';
};

/** Message for the auth-error region; '' for a cancelled popup (nothing went wrong). */
export const providerErrorMessage = (provider, kind) => {
  const m = socialMessages(provider);
  if (kind === 'cancelled') return '';
  if (kind === 'popup_blocked') return m.popupBlocked;
  if (kind === 'unavailable') return m.unavailable;
  return m.generic;
};

/** A failed POST /auth/social -> user-facing message (only a 422 echoes the server detail). */
export const socialErrorMessage = (provider, err) => {
  const m = socialMessages(provider);
  const res = err && err.response;
  const status = res && res.status;
  if (status === 401) return m.unverified;
  if (status === 409) return m.conflict;
  if (status === 503) return m.unavailable;
  if (status === 422) {
    const problem = (res.data && typeof res.data === 'object' && res.data) || {};
    const lines = (Array.isArray(problem.errors) ? problem.errors : [])
      .filter((e) => e && typeof e.message === 'string' && e.message)
      .map((e) => e.message);
    const detail = typeof problem.detail === 'string' && problem.detail ? problem.detail : m.invalid;
    return [detail, ...lines].join(' ');
  }
  return m.generic;
};

/** Rejection value of `socialLogin`: a user-facing `message` plus the HTTP `status` (if any). */
export class SocialLoginError extends Error {
  constructor(message, status = null) {
    super(message);
    this.name = 'SocialLoginError';
    this.status = status;
  }
}

/**
 * Exchanges a provider token for a session and stores it (same storage as password sign-in).
 * Resolves with the session; rejects with a SocialLoginError.
 */
export const socialLogin = async (request, {idempotencyKey = newIdempotencyKey(), http = axios} = {}) => {
  const body = toSocialRequest(request);
  let res;
  try {
    res = await http.post(`${config.apiBaseUrl}${SOCIAL_PATH}`, body, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
    });
  } catch (err) {
    const status = (err && err.response && err.response.status) || null;
    throw new SocialLoginError(socialErrorMessage(body.provider, err), status);
  }
  let session;
  try {
    session = fromLoginResponse(res.data);
  } catch {
    throw new SocialLoginError(socialMessages(body.provider).generic, res.status);
  }
  saveSession(session);
  return session;
};
